import { ReactNode, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import Sidebar from '../components/sidebar/Sidebar';
import Topbar from '../components/topbar/Topbar';
import { useBrand } from '../lib/api/brands';
import { useCampaign } from '../lib/api/campaigns';

interface Crumb {
  label: string;
  to?: string;
}

interface PathInfo {
  brandId?: string;
  brandSubpage?: 'visibility';
  campaignId?: string;
}

const parsePath = (path: string): PathInfo => {
  const brandMatch = path.match(/^\/brands\/([^/]+)(?:\/(visibility))?\/?$/);
  if (brandMatch) {
    return {
      brandId: brandMatch[1],
      brandSubpage: brandMatch[2] === 'visibility' ? 'visibility' : undefined,
    };
  }
  const campaignMatch = path.match(/^\/campaigns\/([^/]+)\/?$/);
  if (campaignMatch) {
    return { campaignId: campaignMatch[1] };
  }
  return {};
};

const buildCrumbs = (
  path: string,
  brandName: string | undefined,
  campaignName: string | undefined,
): Crumb[] => {
  if (path === '/') return [{ label: 'Dashboard' }];
  if (path.startsWith('/my-work')) return [{ label: 'My work' }];
  if (path.startsWith('/campaigns')) {
    if (path === '/campaigns') return [{ label: 'Campaigns' }];
    const info = parsePath(path);
    return [
      { label: 'Campaigns', to: '/campaigns' },
      { label: campaignName || info.campaignId || 'Detail' },
    ];
  }
  if (path.startsWith('/brands')) {
    if (path === '/brands') return [{ label: 'Brands' }];
    const info = parsePath(path);
    const detailLabel = brandName || info.brandId || 'Detail';
    if (info.brandSubpage === 'visibility') {
      return [
        { label: 'Brands', to: '/brands' },
        { label: detailLabel, to: `/brands/${info.brandId}` },
        { label: 'Visibility plan' },
      ];
    }
    return [
      { label: 'Brands', to: '/brands' },
      { label: detailLabel },
    ];
  }
  if (path.startsWith('/social')) return [{ label: 'Social' }];
  if (path.startsWith('/newsletters')) return [{ label: 'Newsletters' }];
  if (path.startsWith('/homepage')) return [{ label: 'Homepage' }];
  if (path.startsWith('/tasks')) return [{ label: 'Tasks' }];
  if (path.startsWith('/notifications')) return [{ label: 'Notifications' }];
  return [];
};

export const AppShell = ({ children }: { children: ReactNode }) => {
  const location = useLocation();
  const info = parsePath(location.pathname);
  // Hooks return undefined data when id is undefined — they're already
  // cached by detail pages, so this read is free in the common case.
  const { data: brand } = useBrand(info.brandId);
  const { data: campaign } = useCampaign(info.campaignId);
  const crumbs = buildCrumbs(location.pathname, brand?.name, campaign?.name);
  // On phones the sidebar is a slide-in drawer; close it on every page change.
  const [navOpen, setNavOpen] = useState(false);
  useEffect(() => setNavOpen(false), [location.pathname]);
  return (
    <div className="app">
      <Sidebar open={navOpen} onClose={() => setNavOpen(false)} />
      {navOpen && <div className="sidebar-backdrop" onClick={() => setNavOpen(false)} />}
      <div className="main">
        <Topbar breadcrumb={crumbs} onMenu={() => setNavOpen(true)} />
        <div className="content">
          <div className="content-inner">{children}</div>
        </div>
      </div>
    </div>
  );
};

export default AppShell;
